import { useEffect, useState } from "react";
import CommonModal from "@/components/common/CommonModal";
import { Button } from "@/components/ui/button";
import type { ISalaryHead } from "@/interface/salaryHead.interface";
import type {
    BranchSalaryHeadType,
    IBranchSalaryHead,
} from "../../interface/branchSalaryHead.types";

interface BranchSalaryHeadModalProps {
    type: BranchSalaryHeadType;
    show: boolean;
    options: ISalaryHead[];
    editingItem: IBranchSalaryHead | null;
    isLoadingOptions: boolean;
    isSubmitting: boolean;
    onClose: () => void;
    onSubmit: (salaryHeadId: string, amount: string, isFixed: boolean) => Promise<boolean>;
}

const typeConfig = {
    earning: {
        title: "Earning",
        optionLabel: "Earning Head",
        optionPlaceholder: "-- Select Earning Head --",
        loadingText: "Loading earnings...",
        amountPlaceholder: "Enter fixed amount (e.g. 5000)",
        accentClass: "focus:ring-emerald-500/20 focus:border-emerald-500",
        checkClass: "text-emerald-600 focus:ring-emerald-500",
        submitVariant: "default" as const,
    },
    deduction: {
        title: "Deduction",
        optionLabel: "Deduction Head",
        optionPlaceholder: "-- Select Deduction Head --",
        loadingText: "Loading deductions...",
        amountPlaceholder: "Enter fixed deduction amount (e.g. 500)",
        accentClass: "focus:ring-rose-500/20 focus:border-rose-500",
        checkClass: "text-rose-600 focus:ring-rose-500",
        submitVariant: "destructive" as const,
    },
};

const BranchSalaryHeadModal: React.FC<BranchSalaryHeadModalProps> = ({
    type,
    show,
    options,
    editingItem,
    isLoadingOptions,
    isSubmitting,
    onClose,
    onSubmit,
}) => {
    const config = typeConfig[type];
    const [salaryHeadId, setSalaryHeadId] = useState("");
    const [amount, setAmount] = useState("");
    const [isFixed, setIsFixed] = useState(true);

    useEffect(() => {
        if (!show) return;

        setSalaryHeadId(editingItem?.salaryHeadId || "");
        setAmount(editingItem?.amount || "");
        setIsFixed(editingItem ? editingItem.isFixed : true);
    }, [editingItem, show]);

    const handleClose = () => {
        setSalaryHeadId("");
        setAmount("");
        setIsFixed(true);
        onClose();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const finalAmount = isFixed ? amount : "0";
        const isSuccess = await onSubmit(salaryHeadId, finalAmount, isFixed);
        if (isSuccess) {
            handleClose();
        }
    };

    return (
        <CommonModal
            title={`${editingItem ? "Edit" : "Add"} ${config.title}`}
            show={show}
            onClose={handleClose}
            className="max-w-lg rounded-2xl p-6"
        >
            <form className="space-y-5 pt-2" onSubmit={handleSubmit}>
                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                        {config.optionLabel} <span className="text-red-500">*</span>
                    </label>
                    <select
                        value={salaryHeadId}
                        onChange={(event) => setSalaryHeadId(event.target.value)}
                        className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 ${config.accentClass} text-sm font-medium text-slate-700 transition-all`}
                        disabled={!!editingItem}
                        required
                    >
                        <option value="">{isLoadingOptions ? config.loadingText : config.optionPlaceholder}</option>
                        {options?.map((item) => (
                            <option key={item?.id} value={item?.id} disabled={item?.isBase}>
                                {item?.name}{item?.isBase ? " (Base)" : ""}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                        Type <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-6 bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <label className="flex items-center gap-2.5 cursor-pointer text-sm font-semibold text-slate-700 select-none">
                            <input
                                type="checkbox"
                                checked={isFixed}
                                onChange={(event) => setIsFixed(event.target.checked)}
                                className={`w-4 h-4 ${config.checkClass} border-slate-300`}
                            />
                            Is Fixed
                        </label>
                        <label className="flex items-center gap-2.5 cursor-pointer text-sm font-semibold text-slate-700 select-none">
                            <input
                                type="checkbox"
                                checked={!isFixed}
                                onChange={(event) => setIsFixed(!event.target.checked)}
                                className={`w-4 h-4 ${config.checkClass} border-slate-300`}
                            />
                            Is Variable
                        </label>
                    </div>
                </div>

                {isFixed && (
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                            Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            value={amount}
                            onChange={(event) => setAmount(event.target.value)}
                            placeholder={config.amountPlaceholder}
                            className={`w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 ${config.accentClass} text-sm font-medium transition-all`}
                            required={isFixed}
                        />
                    </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        className="px-5 rounded-xl font-semibold"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" variant={config.submitVariant} disabled={isSubmitting}>
                        {isSubmitting ? "Saving..." : `${editingItem ? "Update" : "Add"} ${config.title}`}
                    </Button>
                </div>
            </form>
        </CommonModal>
    );
};

export default BranchSalaryHeadModal;
